import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

export interface Container {
  id: number;
  status: string;
  etd: string | null;
  eta: string | null;
  eta_customer: string | null;
  track_and_trace: string | null;
  reference: string | null;
  origin_port: {
    id: number;
    name: string;
    country: string | null;
  } | null;
  destination_port: {
    id: number;
    name: string;
    country: string | null;
  } | null;
}

export const useContainer = (bookingId: string) => {
  const [container, setContainer] = useState<Container | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchContainer = async () => {
      setLoading(true);
      try {
        // First get the booking to check its status and container_id
        const { data: booking, error: bookingError } = await supabase
          .from('bookings')
          .select('status, container_id')
          .eq('id', bookingId)
          .single();

        if (bookingError) throw bookingError;

        // Only fetch container if booking is in relevant status and has a container_id
        if (['approved', 'waiting_for_goods', 'goods_received_origin', 'in_transit', 'custom_clearance', 'out_for_delivery', 'delivered'].includes(booking.status) && booking.container_id) {
          const { data, error } = await supabase
            .from('containers')
            .select(`
              *,
              origin_port:booking_origin_port(
                id,
                name,
                country
              ),
              destination_port:booking_destination_port(
                id,
                name,
                country
              )
            `)
            .eq('id', booking.container_id)
            .single();

          if (error) throw error;
          setContainer(data);
        } else {
          setContainer(null);
        }
      } catch (err) {
        console.error('Error fetching container:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch container');
        setContainer(null);
      } finally {
        setLoading(false);
      }
    };

    fetchContainer();
  }, [bookingId]);

  return { container, loading, error };
}; 